import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import ProgressiveDisclosure from '../shared/ProgressiveDisclosure';
import { RecordCardActionButton } from '../shared/RecordCardControls';
import useViewModePreference from '../shared/useViewModePreference';
import { StaffFullCard, StaffHybridCard, StaffTile } from './StaffCards';
import StaffUserForm from './StaffUserForm';
import { StaffPlacementChart } from './StaffPlacements';
import { formatPhone } from '../../lib/phone';

const BUTTON = 'ia-hover-pulse rounded-full border border-[var(--surface-border)] px-4 py-2 text-sm disabled:opacity-50';
const INPUT = 'rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-3 py-2 text-sm';
const STAFF_VIEW_MODES = ['List', 'Card', 'Hybrid'];
const STAFF_VIEW_STORAGE_KEY = 'crm.staff.viewMode';

export default function StaffPersonnelView({ userId }) {
    const [staff, setStaff] = useState([]);
    const [people, setPeople] = useState([]);
    const [entities, setEntities] = useState([]);
    const [groups, setGroups] = useState([]);
    const [roleTags, setRoleTags] = useState([]);
    const [rolePresets, setRolePresets] = useState([]);
    const [steps, setSteps] = useState([]);
    const [editScope, setEditScope] = useState({ all: false, entities: [], groups: [] });
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState('');
    const [employment, setEmployment] = useState('');
    const [entityId, setEntityId] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loginActionPending, setLoginActionPending] = useState(null);
    const [form, setForm] = useState(null);
    const [canPlaceOrganization, setCanPlaceOrganization] = useState(false);
    const [showChart, setShowChart] = useState(false);
    const [viewMode, setViewMode] = useViewModePreference({
        userId,
        storageKey: STAFF_VIEW_STORAGE_KEY,
        modes: STAFF_VIEW_MODES,
        defaultMode: 'List',
    });
    const loadRequest = useRef(0);

    const load = useCallback(async () => {
        const request = ++loadRequest.current;
        setLoading(true); setError('');
        try {
            const results = await Promise.all([
                supabaseClient.rpc('bl391_staff_directory'),
                supabaseClient.rpc('bl387_personnel_directory'),
                supabaseClient.from('entities').select('id, org_id, name, level_type, parent_entity_id, entity_code').order('name'),
                supabaseClient.from('org_groups').select('id, org_id, entity_id, name').order('name'),
                supabaseClient.from('role_tags').select('id, name, active, sort_order').order('sort_order'),
                supabaseClient.from('onboarding_step_definitions').select('id, label, is_active, context').order('label'),
                supabaseClient.rpc('has_module_access', { p_module: 'personnel', p_action: 'edit', p_entity_id: null }),
                supabaseClient.rpc('bl392_staff_mail_status'),
                supabaseClient.rpc('bl396a_owner_or_super_admin'),
                supabaseClient.from('org_role_presets').select('id, org_id, key, name, area_levels, show_trip_prices').order('name'),
                supabaseClient.rpc('bl395a_staff_access_directory'),
            ]);
            const firstError = results.find((result) => result.error)?.error;
            if (firstError) throw firstError;
            const [staffResult, peopleResult, entityResult, groupResult, tagsResult, stepResult, editResult, mailResult, ownerResult, presetResult, accessResult] = results;
            const accessByStaff = new Map((Array.isArray(accessResult.data) ? accessResult.data : [])
                .map((item) => [item.staff_id, item.command_center_preset_id]));
            const mailByStaff = new Map((Array.isArray(mailResult.data) ? mailResult.data : [])
                .map((item) => [item.staff_id, item.last_invite_sent_at]));
            const profileIds = [...new Set((staffResult.data || [])
                .map((member) => member.user_profile_id)
                .filter(Boolean))];
            let avatars = [];
            if (profileIds.length) {
                const { data, error: avatarError } = await supabaseClient
                    .from('user_profiles')
                    .select('id, avatar_url')
                    .in('id', profileIds);
                if (avatarError) throw avatarError;
                avatars = data || [];
            }
            const avatarByProfile = new Map(avatars.map((profile) => [profile.id, profile.avatar_url]));
            let scope = { all: editResult.data === true, entities: [], groups: [] };
            if (!scope.all) {
                const targets = [
                    ...(entityResult.data || []).map((item) => ({ type: 'entities', id: item.id, entity: item.id })),
                    ...(groupResult.data || []).map((item) => ({ type: 'groups', id: item.id, entity: item.entity_id }))
                ];
                const access = await Promise.all(targets.map(async (target) => {
                    const { data, error: accessError } = await supabaseClient.rpc('has_module_access', {
                        p_module: 'personnel', p_action: 'edit', p_entity_id: target.entity,
                        ...(target.type === 'groups' ? { p_group_id: target.id } : {})
                    });
                    if (accessError) throw accessError;
                    return { ...target, allowed: data === true };
                }));
                scope = { ...scope,
                    entities: access.filter((item) => item.allowed && item.type === 'entities').map((item) => item.id),
                    groups: access.filter((item) => item.allowed && item.type === 'groups').map((item) => item.id) };
            }
            if (request !== loadRequest.current) return;
            setStaff((staffResult.data || []).map((member) => ({
                ...member,
                command_center_preset_id: accessByStaff.get(member.id) || null,
                avatar_url: avatarByProfile.get(member.user_profile_id) || null,
                last_invite_sent_at: mailByStaff.get(member.id) || null,
            }))); setPeople(peopleResult.data || []);
            setEntities(entityResult.data || []); setGroups(groupResult.data || []);
            setRoleTags(tagsResult.data || []); setSteps((stepResult.data || []).filter((item) => item.is_active && item.context === 'driver'));
            setRolePresets(presetResult.data || []);
            setEditScope(scope);
            setCanPlaceOrganization(ownerResult.data === true);
        } catch (e) {
            if (request !== loadRequest.current) return;
            setError(e.message || 'Staff could not be loaded.');
            setStaff([]); setPeople([]); setEntities([]); setGroups([]); setRoleTags([]); setSteps([]); setRolePresets([]);
            setEditScope({ all: false, entities: [], groups: [] });
            setCanPlaceOrganization(false);
        }
        finally { if (request === loadRequest.current) setLoading(false); }
    }, []);
    useEffect(() => {
        setForm(null); load();
        return () => { loadRequest.current += 1; };
    }, [load, userId]);
    const canEdit = (entity, group) => editScope.all || editScope.entities.includes(entity) || editScope.groups.includes(group);
    const canCreate = editScope.all || editScope.entities.length > 0 || editScope.groups.length > 0;
    const updateTripPriceVisibility = async (presetId, showTripPrices) => {
        const preset = rolePresets.find((item) => item.id === presetId);
        if (!preset) throw new Error('The selected role preset is no longer available.');
        const { error: updateError } = await supabaseClient
            .from('org_role_presets')
            .update({ show_trip_prices: showTripPrices })
            .eq('id', preset.id)
            .eq('org_id', preset.org_id);
        if (updateError) throw updateError;
        setRolePresets((current) => current.map((item) =>
            item.id === presetId ? { ...item, show_trip_prices: showTripPrices } : item
        ));
    };
    const entityMap = useMemo(() => new Map(entities.map((item) => [item.id, item])), [entities]);
    const groupMap = useMemo(() => new Map(groups.map((item) => [item.id, item])), [groups]);
    const tagMap = useMemo(() => new Map(roleTags.map((item) => [item.id, item])), [roleTags]);
    const companyFor = (member) => {
        const placement = entityMap.get(member.entity_id);
        return placement?.level_type === 'market' ? entityMap.get(placement.parent_entity_id) : placement;
    };
    const companyLabel = (member) => companyFor(member)?.name || 'No Company';
    const marketLabel = (member) => entityMap.get(member.entity_id)?.level_type === 'market'
        ? entityMap.get(member.entity_id)?.name || '' : '';
    const departmentLabel = (member) => groupMap.get(member.group_id)?.name || 'None';
    const roleTagLabel = (member) => tagMap.get(member.role_tag_id)?.name || 'None';
    const filtered = staff.filter((member) => {
        const haystack = [member.full_name, member.email, member.phone, formatPhone(member.phone), member.title,
            entityMap.get(member.entity_id)?.name, groupMap.get(member.group_id)?.name,
            tagMap.get(member.role_tag_id)?.name].join(' ').toLowerCase();
        return haystack.includes(query.trim().toLowerCase()) && (!status || member.status === status)
            && (!employment || member.employment_type === employment) && (!entityId || member.entity_id === entityId);
    });
    const unplaced = people.filter((person) => !staff.some((member) =>
        (person.person_id && member.person_id === person.person_id)
        || (person.user_profile_id && member.user_profile_id === person.user_profile_id)
        || member.email?.trim().toLowerCase() === person.email?.trim().toLowerCase())
        && `${person.full_name} ${person.email} ${person.phone} ${formatPhone(person.phone)}`.toLowerCase().includes(query.trim().toLowerCase()));
    const openPerson = async (person) => {
        const member = staff.find((item) => item.id === person.staff_id
            || (person.person_id && item.person_id === person.person_id)
            || (person.user_profile_id && item.user_profile_id === person.user_profile_id));
        if (member && !canEdit(member.entity_id, member.group_id)) {
            setError('This record is outside your Personnel edit scope.'); setForm(null); return;
        }
        if (member) { setForm({ staff: member }); return; }
        if (person.user_profile_id && person.is_member) {
            const { data, error: accessError } = await supabaseClient.rpc('org_member_directory');
            if (accessError) { setError(`Could not load this person's existing app access: ${accessError.message}`); return; }
            const existing = data?.find((item) => item.id === person.user_profile_id);
            if (!existing) { setError('This membership changed. Refresh the people directory before editing.'); return; }
            const existingStaff = staff.find((item) => item.user_profile_id === person.user_profile_id);
            const { data: assignments, error: assignmentError } = await supabaseClient
                .from('role_assignments')
                .select('entity_id, role_preset_id')
                .eq('org_id', existing.org_id)
                .eq('grantee_type', 'user')
                .eq('grantee_id', person.user_profile_id)
                .not('role_preset_id', 'is', null);
            if (assignmentError) { setError(`Could not load this person's role preset: ${assignmentError.message}`); return; }
            const userAssignments = assignments || [];
            const assignedEntities = [...new Set(userAssignments.map((item) => item.entity_id).filter(Boolean))];
            setForm({ person: { ...person, command_center_role: existing.role,
                command_center_entity_ids: existingStaff?.command_center_entity_ids
                    || (assignedEntities.length ? assignedEntities : (existing.entity_id ? [existing.entity_id] : [])),
                command_center_preset_id: existingStaff?.command_center_preset_id
                    || userAssignments[0]?.role_preset_id || null } });
        } else setForm({ person });
    };

    const saveCardStaff = async (member, draft) => {
        if (!canEdit(member.entity_id, member.group_id)) throw new Error('This record is outside your Personnel edit scope.');
        const currentHome = entityMap.get(member.entity_id);
        const organizationHomeLocked = currentHome?.level_type === 'organization' && !canPlaceOrganization;
        const homeEntityId = organizationHomeLocked
            ? member.entity_id : draft.market_id || draft.company_id || draft.entity_id;
        if (!homeEntityId) throw new Error('Choose a Company.');
        const { data, error: saveError } = await supabaseClient.rpc('bl391_save_staff', {
            p_staff_id: member.id,
            p_expected_person: member.person_id || member.user_profile_id || null,
            p_record: {
                entity_id: homeEntityId,
                secondary_entity_ids: (draft.secondary_entity_ids || member.secondary_entity_ids || [])
                    .filter((id) => id !== homeEntityId),
                group_id: draft.group_id || null,
                title: draft.title?.trim() || '',
                employment_type: draft.employment_type || null,
                role_tag_id: draft.role_tag_id || null,
                status: draft.status,
                person_id: member.person_id || null,
                user_profile_id: member.person_id ? null : member.user_profile_id || null,
            },
            p_app_access: {},
        });
        if (saveError) throw saveError;
        if (!data?.staff_id) throw new Error('The staff record was not saved.');
        // Reports to (migration 259) is its own locked HR call; only sent when it changed and the database says this viewer may.
        if (member.reports_to_can_change === true && draft.reports_to_staff_id !== undefined
            && (draft.reports_to_staff_id || null) !== (member.reports_to_staff_id || null)) {
            const { error: reportsToError } = await supabaseClient.rpc('staff_set_reports_to', {
                p_staff_id: member.id, p_reports_to_staff_id: draft.reports_to_staff_id || null,
            });
            if (reportsToError) { await load(); throw new Error(`Reports to was not saved: ${reportsToError.message}`); }
        }
        await load();
    };

    const changeEmployeeId = async (member, employeeId, reason) => {
        const { error: changeError } = await supabaseClient.rpc('bl396a_change_employee_id', {
            p_staff_id: member.id, p_employee_id: employeeId, p_reason: reason, p_expected_id: member.employee_id,
        });
        if (changeError) throw changeError;
        await load();
    };

    const archiveCardStaff = async (member) => {
        await saveCardStaff(member, {
            entity_id: member.entity_id,
            company_id: companyFor(member)?.id || '',
            market_id: marketLabel(member) ? member.entity_id : '',
            group_id: member.group_id,
            title: member.title,
            employment_type: member.employment_type,
            role_tag_id: member.role_tag_id,
            status: 'inactive',
        });
    };

    const loginAction = async (member, action) => {
        if (loginActionPending) return;
        setError(''); setNotice('');
        setLoginActionPending(member.id);
        try {
            if (!member.email) throw new Error('This staff member has no login email. Open their record to review app access.');
            if (action !== 'reset' && !member.command_center_enabled && !member.applicant_id) {
                throw new Error('This staff member has no linked Driver App invitation. Open their record to review app access.');
            }
            const endpoint = action === 'reset' || member.command_center_enabled ? 'team-invite' : 'driver-application-auth';
            const body = action === 'reset' ? { action: 'password_reset', staffId: member.id }
                : member.command_center_enabled ? {
                    action: 'invite', resend: true, staffId: member.id, email: member.email,
                    role: member.command_center_role, entityIds: member.command_center_entity_ids || [],
                } : { action: 'provision_login', resend: true, applicantId: member.applicant_id };
            const { data, error: requestError } = await supabaseClient.functions.invoke(endpoint, { body });
            let result = data;
            if (requestError?.context && typeof requestError.context.json === 'function') result = await requestError.context.json();
            if (requestError || result?.error || result?.ok === false) throw new Error(result?.error || requestError?.message || 'Login action failed.');
            if (result?.sent !== true) throw new Error("No email was sent. Review this staff member's app access and try again.");
            setNotice(action === 'reset' ? `Reset link sent to ${result.email || member.email}` : `Invite sent to ${result.email || member.email}`);
            if (action !== 'reset') setStaff((current) => current.map((item) => item.id === member.id
                ? { ...item, last_invite_sent_at: result.inviteSentAt } : item));
        } catch (e) { setError(e.message); }
        finally { setLoginActionPending(null); }
    };

    if (loading) return <p className="p-8">Loading people...</p>;
    return <div className="space-y-4 text-[var(--ink)]">
        {error && <div role="alert">{error}<button className={BUTTON} onClick={load}>Retry loading</button></div>}
        {notice && <p role="status">{notice}</p>}
        <div className="flex justify-between gap-4"><div><h2 className="text-xl font-semibold">People</h2>
            <p className="text-sm text-[var(--ink-muted)]">Search existing people. Add staff with or without app access.</p></div>
            {canCreate && <button className={BUTTON} onClick={() => setForm({})}>New Staff/User</button>}
        </div>
        <div className="flex flex-wrap gap-3">
            <input className={INPUT} aria-label="Search existing people" type="search" placeholder="Search existing people" value={query} onChange={(event) => setQuery(event.target.value)} />
            <select className={INPUT} aria-label="Filter status" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">All Statuses</option>{['active', 'inactive', 'on_leave', 'terminated'].map((value) =>
                    <option key={value} value={value}>{value.replace('_', ' ')}</option>)}
            </select>
            <select className={INPUT} aria-label="Filter employment" value={employment} onChange={(event) => setEmployment(event.target.value)}>
                <option value="">All Employment Types</option><option value="w2_staff">W-2 Employee</option><option value="1099_contractor">1099 Contractor</option>
            </select>
            <select className={INPUT} aria-label="Filter Company/Market" value={entityId} onChange={(event) => setEntityId(event.target.value)}>
                <option value="">All Companies/Markets</option>{entities.filter((item) => item.level_type !== 'organization').map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
        </div>
        <div className="flex justify-end gap-2" role="group" aria-label="Staff view">
            {STAFF_VIEW_MODES.map((mode) => (
                <RecordCardActionButton
                    key={mode}
                    aria-pressed={viewMode === mode}
                    onClick={() => setViewMode(mode)}
                    className={viewMode === mode ? 'ia-selected-pill border-[var(--selected-pill-border)] text-[var(--selected-pill-ink)]' : 'text-[var(--ink-muted)]'}
                >
                    {mode}
                </RecordCardActionButton>
            ))}
        </div>
        {!filtered.length && !unplaced.length && <p>No people match this view.</p>}
        <button className={BUTTON} aria-pressed={showChart} onClick={() => setShowChart((value) => !value)}>Placement org chart</button>
        {showChart && <StaffPlacementChart staff={filtered} entities={entities} />}
        <div className={viewMode === 'Card' ? 'grid gap-3 sm:grid-cols-2' : 'space-y-3'}>
            {filtered.map((member) => (
                <StaffDisclosure
                    key={member.id}
                    member={{ ...member, person: {
                        full_name: member.full_name, email: member.email, phone: member.phone,
                        avatar_url: member.avatar_url,
                        has_driver: Boolean(member.driver_id),
                    }, driver: member.driver_id ? { id: member.driver_id, status: member.driver_status } : null }}
                    mode={viewMode}
                    entities={entities}
                    groups={groups}
                    roleTags={roleTags}
                    rolePresets={rolePresets}
                    statusOptions={['active', 'inactive', 'on_leave', 'terminated'].map((value) => ({ value, label: value.replace('_', ' ') }))}
                    employmentOptions={[
                        { value: '', label: 'Unclassified' },
                        { value: 'w2_staff', label: 'W-2 Employee' },
                        { value: '1099_contractor', label: '1099 Contractor' },
                    ]}
                    canEditTarget={canEdit}
                    canPlaceOrganization={canPlaceOrganization}
                    staffDirectory={staff}
                    onChangeEmployeeId={(id, reason) => changeEmployeeId(member, id, reason)}
                    companyLabel={companyLabel}
                    marketLabel={marketLabel}
                    departmentLabel={departmentLabel}
                    roleTagLabel={roleTagLabel}
                    canEdit={canEdit(member.entity_id, member.group_id)}
                    onSave={(draft) => saveCardStaff(member, draft)}
                    onOpenEdit={() => setForm({ staff: member })}
                    onArchive={() => archiveCardStaff(member)}
                    onGiveAppAccess={() => setForm({ staff: member })}
                    onSendDriverInvite={() => setForm({ staff: member })}
                    onResendInvite={() => loginAction(member, 'resend')}
                    onSendPasswordReset={() => loginAction(member, 'reset')}
                    loginActionPending={Boolean(loginActionPending)}
                />
            ))}
        </div>
        {unplaced.map((person) => <article key={person.id} className="rounded-xl border border-[var(--surface-border)] p-4">
            <h3 className="font-semibold">{person.full_name || person.email}</h3><p>{person.email} · {formatPhone(person.phone)}</p>
            <p>No staff placement</p>{canCreate && <button className={BUTTON} onClick={() => openPerson(person)}>Open their record</button>}
        </article>)}
        {form && <StaffUserForm key={form.staff?.id || form.person?.id || 'new'} {...form}
            entities={entities} groups={groups} roleTags={roleTags} rolePresets={rolePresets}
            stepDefinitions={steps} canEditTarget={canEdit} canPlaceOrganization={canPlaceOrganization} staffDirectory={staff}
            onUpdateTripPriceVisibility={updateTripPriceVisibility}
            onClose={() => { setForm(null); load(); }} onSaved={async () => { setForm(null); await load(); }}
            onOpenPerson={openPerson} />}
    </div>;
}

function StaffDisclosure({
    member, mode, entities, groups, roleTags, statusOptions, employmentOptions, staffDirectory = [],
    companyLabel, marketLabel, departmentLabel, roleTagLabel, canEdit, canEditTarget, canPlaceOrganization, onChangeEmployeeId,
    onSave, onOpenEdit, onArchive, onGiveAppAccess, onSendDriverInvite, onResendInvite, onSendPasswordReset, loginActionPending,
}) {
    const createDraft = (record) => {
        const placement = entities.find((entity) => entity.id === record.entity_id);
        const company = placement?.level_type === 'market'
            ? entities.find((entity) => entity.id === placement.parent_entity_id) : placement;
        return {
            company_id: company?.id || '',
            market_id: placement?.level_type === 'market' ? placement.id : '',
            entity_id: record.entity_id || '',
            secondary_entity_ids: record.secondary_entity_ids || [],
            group_id: record.group_id || '',
            title: record.title || '',
            employment_type: record.employment_type || '',
            role_tag_id: record.role_tag_id || '',
            status: record.status || 'active',
            reports_to_staff_id: record.reports_to_staff_id || '',
        };
    };
    const initialTier = mode === 'Hybrid' ? 2 : 1;
    const statusLabel = (value) => statusOptions.find((option) => option.value === value)?.label || value;
    const employmentLabel = (value) => employmentOptions.find((option) => option.value === value)?.label || 'Unclassified';
    const common = {
        member, entities, groups, roleTags, statusOptions, employmentOptions, canEditTarget,
        canPlaceOrganization, onChangeEmployeeId, staffDirectory,
        companyLabel, marketLabel, departmentLabel, roleTagLabel, statusLabel, employmentLabel, canEdit,
        onOpenEdit, onArchive, onGiveAppAccess, onSendDriverInvite, onResendInvite, onSendPasswordReset, loginActionPending,
    };
    return (
        <ProgressiveDisclosure
            record={member}
            initialTier={initialTier}
            createDraft={createDraft}
            onSave={onSave}
            renderTier1={({ showPeek, showFull }) => mode === 'List' ? (
                <button type="button" onClick={showPeek} className={`${BUTTON} w-full text-left`}>
                    {member.full_name || 'Person not linked'} · {member.title} · {companyLabel(member)}
                    {marketLabel(member) ? ` / ${marketLabel(member)}` : ''}
                </button>
            ) : (
                <StaffTile
                    {...common}
                    onOpen={mode === 'Card' ? showFull : showPeek}
                />
            )}
            renderTier2={({ showFull, collapse }) => (
                <StaffHybridCard
                    {...common}
                    onShowMore={showFull}
                    onCollapseAll={collapse}
                />
            )}
            renderTier3={(editor) => (
                <StaffFullCard
                    {...common}
                    editor={editor}
                    onCollapse={editor.collapse}
                    onCollapseAll={editor.collapse}
                />
            )}
        />
    );
}
